import { DATE_FORMAT } from '@constants';
import { format } from 'date-fns';
import { useEffect, useMemo, useRef } from 'react';
import type { Path } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type {
	FXCurrencyFormConfigEntry,
	FXFormFieldConfig,
	FXPayment,
	PAYMENT_TYPE,
} from 'types/operations';
import { FxRemittanceType } from 'types/operations';

import { useOnCurrencyChange } from './useOnCurrencyChange';

export interface FXFormBeneficiary {
	name?: string;
	account?: string;
	bank_routing_number?: string;
	address?: {
		type?: 'STRUCTURED';
		line?: string;
		town?: string;
		country?: string;
		postal_code?: string;
		building_number?: string;
	};
	bank?: {
		name?: string;
		bic?: string;
		address?: {
			type?: 'STRUCTURED';
			line?: string;
			town?: string;
			country?: string;
			postal_code?: string;
			building_number?: string;
		};
	};
}

export interface FXFormValues {
	payment_type: PAYMENT_TYPE;
	currency?: string;
	amount?: number;
	account_uuid?: string;
	account?: string;
	fx_currency?: string;
	fx_amount?: number;
	fx_quote?: string;
	fx_remittance_type?: FxRemittanceType;
	payment_urgency?: 'NORMAL' | 'FAST' | 'URGENT';
	payment_details?: string;
	payment_details_type?: string;
	document_number?: string;
	execution_date?: string;
	beneficiary?: FXFormBeneficiary;
	payer?: unknown;
	correspondent?: unknown;
	ultimate_beneficiary?: unknown;
}

// Holds the constant 'STRUCTURED' discriminator, not user input — never touched.
const isAddressTypeField = (key: string): boolean =>
	key === 'beneficiary.address.type' || key === 'beneficiary.bank.address.type';

type BeneficiaryPath = Path<FXFormValues>;

interface BeneficiaryCorridorShape {
	defaults: Map<BeneficiaryPath, string>;
	rendered: Set<BeneficiaryPath>;
}

// Collects the pinned defaults and rendered beneficiary leaves of the active view
// in one pass. Only `general` + the active payment-type section are scanned, since
// only those are rendered (see `allFields`) — a leaf in the inactive section is
// off-screen and must not count as rendered, else its stale value would persist.
const collectBeneficiaryCorridorShape = (
	paymentType: FxRemittanceType,
	classifier?: FXCurrencyFormConfigEntry,
): BeneficiaryCorridorShape => {
	const defaults = new Map<BeneficiaryPath, string>();
	const rendered = new Set<BeneficiaryPath>();
	if (!classifier) {
		return { defaults, rendered };
	}
	(['general', paymentType] as const).forEach((section) => {
		const fields = classifier.config[section]?.fields;
		if (!fields) {
			return;
		}
		Object.entries(fields).forEach(([key, config]) => {
			if (!key.startsWith('beneficiary.') || isAddressTypeField(key)) {
				return;
			}
			const path = key as BeneficiaryPath;
			rendered.add(path);
			if (config.value !== undefined) {
				defaults.set(path, config.value);
			}
		});
	});
	return { defaults, rendered };
};

const mapFXOperationToFormValues = (operation: FXPayment): FXFormValues => ({
	...operation,
	currency: operation.fx_quote?.currency_from,
	fx_currency: operation.fx_quote?.currency_to,
	fx_quote: operation.fx_quote?.id,
	execution_date: operation.execution_date ?? undefined,
	beneficiary: {
		...operation.beneficiary,
		account: operation.beneficiary?.account_number,
		bank_routing_number: operation.beneficiary?.bank_routing_number,
		address: operation.beneficiary?.address ? operation.beneficiary.address : undefined,
	},
});

interface UseFXFormStateProps {
	classifiers: FXCurrencyFormConfigEntry[];
	operation?: FXPayment;
}

export const useFXFormState = ({ classifiers, operation }: UseFXFormStateProps) => {
	const fxTransferFrom = useForm<FXFormValues>({
		mode: 'onChange',
		defaultValues: {
			fx_remittance_type: FxRemittanceType.SWIFT,
			payment_urgency: 'NORMAL',
			payment_type: 'FX' as PAYMENT_TYPE,
			execution_date: format(new Date(), DATE_FORMAT),
			...(operation ? mapFXOperationToFormValues(operation) : {}),
			document_number: undefined,
		},
	});
	const isoCodes = useMemo(
		() => classifiers.map((c) => ({ value: c.iso_code, label: c.iso_code })),
		[classifiers],
	);

	const [fxCurrency, accountUuid, paymentType] = fxTransferFrom.watch([
		'fx_currency',
		'account_uuid',
		'fx_remittance_type',
	] as const);

	useEffect(() => {
		if (!fxCurrency && isoCodes[0]?.value) {
			fxTransferFrom.setValue('fx_currency', isoCodes[0].value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isoCodes, fxCurrency]);

	const selectedClassifier = useMemo(
		() => classifiers.find((c) => c.iso_code === fxCurrency),
		[classifiers, fxCurrency],
	);

	// Path -> value the previous reconcile pinned from config (not user input), so a
	// stale pin (e.g. USD's "US" country) can be cleared once the next corridor stops
	// pinning it. The value lets us leave a pin the user manually edited untouched.
	const pinnedDefaultsRef = useRef<Map<BeneficiaryPath, string>>(new Map());

	// Reconcile beneficiary fields on corridor change (currency OR payment type, both
	// change which fields render). Per leaf: apply a new pin; else clear if no longer
	// rendered or a stale pin; else keep what the user typed.
	useOnCurrencyChange(`${fxCurrency ?? ''}:${paymentType ?? ''}`, () => {
		const { defaults, rendered } = collectBeneficiaryCorridorShape(
			paymentType as FxRemittanceType,
			selectedClassifier,
		);
		const wasPinned = pinnedDefaultsRef.current;
		const nextPinned = new Map<BeneficiaryPath, string>();
		const keptNonEmpty: BeneficiaryPath[] = [];
		// Visit rendered leaves plus previously-touched ones, so dropped leaves clear.
		new Set([...rendered.keys(), ...wasPinned.keys()]).forEach((path) => {
			const pinned = defaults.get(path);
			if (pinned !== undefined) {
				nextPinned.set(path, pinned);
				fxTransferFrom.setValue(path, pinned, {
					shouldValidate: false,
					shouldDirty: false,
				});
				return;
			}
			const stalePin =
				wasPinned.has(path) && wasPinned.get(path) === fxTransferFrom.getValues(path);
			if (!rendered.has(path) || stalePin) {
				fxTransferFrom.setValue(path, '', {
					shouldValidate: false,
					shouldDirty: false,
				});
				return;
			}
			if (fxTransferFrom.getValues(path)) {
				keptNonEmpty.push(path);
			}
		});
		pinnedDefaultsRef.current = nextPinned;
		// Re-validate only kept non-empty leaves, so a carried-over invalid value
		// re-errors without freshly-cleared required fields erroring prematurely.
		fxTransferFrom.clearErrors('beneficiary');
		if (keptNonEmpty.length > 0) {
			void fxTransferFrom.trigger(keptNonEmpty);
		}
	});

	const hasLocal = !!selectedClassifier?.config?.local;
	const hasSwift = !!selectedClassifier?.config?.swift;
	const hasBoth = hasLocal && hasSwift;

	useEffect(() => {
		fxTransferFrom.setValue(
			'fx_remittance_type',
			hasLocal ? FxRemittanceType.LOCAL : FxRemittanceType.SWIFT,
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasLocal, hasSwift, selectedClassifier]);

	const typeFields = useMemo((): [string, FXFormFieldConfig][] => {
		if (!selectedClassifier) {
			return [];
		}
		const fields = selectedClassifier.config[paymentType as 'local' | 'swift']?.fields;
		if (!fields) {
			return [];
		}
		return Object.entries(fields as Record<string, FXFormFieldConfig>);
	}, [selectedClassifier, paymentType]);

	const generalFields = useMemo((): [string, FXFormFieldConfig][] => {
		const fields = selectedClassifier?.config?.general?.fields;
		if (!fields) {
			return [];
		}
		return Object.entries(fields as Record<string, FXFormFieldConfig>);
	}, [selectedClassifier]);

	const fxQuoteField: [string, FXFormFieldConfig] = [
		'fx_quote',
		{ type: 'string', label: 'fx_quote', required: false, hidden: true },
	];

	const allFields = useMemo(
		() => {
			const typeFieldKeys = new Set(typeFields.map(([key]) => key));
			const filteredGeneralFields = generalFields.filter(
				([key]) => !typeFieldKeys.has(key),
			);
			return [...typeFields, ...filteredGeneralFields]
				.sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0))
				.concat([fxQuoteField]);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[typeFields, generalFields],
	);

	return {
		fxTransferFrom,
		isoCodes,
		fxCurrency,
		accountUuid,
		paymentType,
		selectedClassifier,
		hasLocal,
		hasSwift,
		hasBoth,
		typeFields,
		generalFields,
		allFields,
	};
};
